import {
  Avatar,
  Badge,
  DataGrid,
  EmptyState,
  LoadingIndicator,
  Pagination,
  Search,
  TextLink,
} from "@abb/arcadia-ui-react"
import { BadgeStatus } from "@abb/arcadia-ui-react/build/components/badge/types"
import { RowData } from "@abb/arcadia-ui-react/build/components/data-grid/types"
import { SelectChangeEvent } from "@mui/material"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import noData from "../../../assets/logos/noData.svg"
import {
  getCellString,
  compareRowValues,
  resolveNextSort,
  parseRowsPerPage,
} from "../../utils/entityTabHelpers"

export type EntityRow = RowData

type EntityCol = {
  id?: string
  field?: string
  headerName?: string
  label?: string
  type?: string
  sortable?: boolean
  numeric?: boolean
  actions?: boolean
  enableSort?: boolean
  isLink?: boolean
  linkBasePath?: string
  isBadge?: boolean
  badgeStatusField?: string
  isAvatar?: boolean
  avatarField?: string
  sortComparator?: (a: string, b: string) => number
  renderCell?: (row: EntityRow) => React.ReactNode
  customHeader?: React.ReactNode
  columnMinWidth?: string
  columnMaxWidth?: string
  className?: string
}

export function EntityTab({
  rows,
  cols,
  isLoading,
  customEmptyGridMessage,
  toolbar,
  actionItems,
  onActionMenuClick,
}: {
  readonly rows: EntityRow[]
  readonly cols: EntityCol[]
  readonly isLoading: boolean
  readonly customEmptyGridMessage?: string
  readonly toolbar?: React.ReactNode
  readonly actionItems?: Array<{ title: string; iconName?: string }>
  readonly onActionMenuClick?: (
    item: { title: string; iconName?: string },
    row: EntityRow
  ) => void
}) {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [searchTerm, setSearchTerm] = useState("")
  const [sortColumn, setSortColumn] = useState("")
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc")
  const navigate = useNavigate()
  const enableActions = Boolean(actionItems && actionItems.length > 0)

  const handleLinkClick = useCallback(
    (linkPath: string) => () => navigate(linkPath),
    [navigate]
  )

  const handleDataGridActionMenuClick = useCallback(
    (item: { title: string }, row: EntityRow) => {
      if (onActionMenuClick) {
        onActionMenuClick(item, row)
      }
    },
    [onActionMenuClick]
  )

  const handleSortChange = useCallback(
    (sort: { column: string; direction: "asc" | "desc" }) => {
      const next = resolveNextSort(sort, sortColumn, sortDirection)
      setSortColumn(next.column)
      setSortDirection(next.direction)
    },
    [sortColumn, sortDirection]
  )

  const handleChangePage = useCallback(
    (_: unknown, newPage: number) => setPage(newPage),
    []
  )

  const handleChangeRowsPerPage = useCallback(
    (
      event:
        | SelectChangeEvent<number>
        | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      const rawValue = "target" in event ? event.target.value : event
      const value = parseRowsPerPage(rawValue)

      if (value != null) {
        setRowsPerPage(value)
        setPage(0)
      }
    },
    []
  )

  const headerData = cols
    .map((c) => ({
      id: c.field ?? c.id ?? "",
      label: c.headerName ?? c.label ?? c.field ?? c.id ?? "",
      numeric: c.numeric ?? c.type === "number",
      actions: c.actions ?? false,
      enableSort: c.enableSort ?? c.sortable !== false,
      customHeader: c.customHeader,
      columnMinWidth: c.columnMinWidth,
      columnMaxWidth: c.columnMaxWidth,
      className: c.className,
    }))
    .filter((h) => h.id)

  const filteredRows = useMemo(() => {
    const terms = searchTerm.trim().toLowerCase().split(/\s+/).filter(Boolean)

    if (terms.length === 0) {
      return rows
    }

    return rows.filter((r) => {
      const rowString = Object.values(r)
        .map((v) => getCellString(v).toLowerCase())
        .join(" ")

      return terms.every((term) => rowString.includes(term))
    })
  }, [rows, searchTerm])

  useEffect(() => {
    setPage(0)
  }, [filteredRows])

  const sortedRows = useMemo(
    () => sortColumn
      ? [...filteredRows].sort((a, b) => {
          const col = cols.find((c) => (c.field ?? c.id) === sortColumn)
          const result = compareRowValues(a[sortColumn], b[sortColumn], col)
          return sortDirection === "desc" ? -result : result
        })
      : filteredRows,
    [filteredRows, sortColumn, sortDirection, cols]
  )

  const processedRows = useMemo(
    () =>
      sortedRows.map((row) => {
        const newRow: EntityRow = { ...row }

        for (const col of cols) {
          const field = col.field ?? ""
          const value = row?.[field]

          if (col.isLink && col.linkBasePath) {
            const displayText = col.renderCell
              ? String(col.renderCell(row))
              : String(value)
            newRow[field] = (
              <TextLink
                text={displayText}
                size={14}
                onClick={handleLinkClick(`${col.linkBasePath}/${row.id}`)}
              />
            )
          } else if (col.renderCell) {
            newRow[field] = col.renderCell(row) as EntityRow[string]
          } else {
            // No transformation needed — keep the original value
          }

          if (col.isBadge) {
            const rawStatus = row[col.badgeStatusField ?? "status"] ?? "primary"
            const status = rawStatus as BadgeStatus

            newRow[field] = (
              <Badge
                label={String(value)}
                status={status}
                variant="filled-square-badge"
              />
            )
          }

          if (col.isAvatar) {
            const avatarField = col.avatarField ?? field
            const initials = String(row[avatarField] ?? "")

            newRow[field] = (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Avatar
                  initials={initials.substring(0, 2).toUpperCase()}
                  size={24}
                />
                <span>{value}</span>
              </div>
            )
          }
        }

        return newRow
      }),
    [sortedRows, cols, handleLinkClick]
  )

  if (isLoading) {
    return (
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "60vh",
        }}
      >
        <LoadingIndicator variant="lazy-load" size="small" />
      </div>
    )
  }

  return (
    <div className="content-container">
      <div className="right-content">
        <div
          className="table"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            width: "100%",
            paddingTop: 16,
          }}
        >
          <div
            className="table-header"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {toolbar}
              </div>

              <div style={{ flexShrink: 0, width: "250px" }}>
                <Search
                  id="entity-tab-search"
                  size={40}
                  placeholder="Search..."
                  variant="discreet"
                  value={searchTerm}
                  onChange={setSearchTerm}
                />
              </div>
            </div>
          </div>
          {processedRows.length === 0 ? (
            <div className="empty-state-content">
              <EmptyState
                imagePath={noData}
                primaryText={customEmptyGridMessage ?? "No data available"}
              />
            </div>
          ) : (
            <>
              <DataGrid
                page={page}
                enableCheckbox={false}
                headerData={headerData}
                name="entity-grid"
                rows={processedRows}
                rowsPerPage={rowsPerPage}
                size={48}
                addActions={enableActions}
                actionItems={actionItems ?? []}
                handleActionMenuClick={handleDataGridActionMenuClick}
                onSortChange={handleSortChange}
              />
              <Pagination
                addBorder
                count={processedRows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
              />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
